import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAccount } from "../../contexts/AccountContext";
import { getUserData } from "../../utils/userUtils";
import { api, getApiUrl } from "../../utils/apiConfig";
import CopyStepper from "../CopyCampaignWorkflow/CopyStepper";
import "../CopyCampaignWorkflow/copy-workflow.css";
import "./update-creative.css";

const steps = ["Upload & references", "Confirm & adjust", "Review & apply"];
const label = {
  name: "Name",
  headlines: "Headlines",
  longHeadlines: "Long headlines",
  descriptions: "Descriptions",
  finalUrls: "Final URLs",
  finalMobileUrls: "Mobile URLs",
  path1: "Display path 1",
  path2: "Display path 2",
  businessName: "Business name",
  callToActionText: "Call to action",
  callToAction: "Call to action",
  trackingUrlTemplate: "Tracking template",
  finalUrlSuffix: "Final URL suffix",
  urlCustomParameters: "URL custom parameters",
};
const multiline = new Set([
  "headlines",
  "longHeadlines",
  "descriptions",
  "finalUrls",
  "finalMobileUrls",
]);
const arrayValue = (value) =>
  Array.isArray(value)
    ? value
        .map((x) => (typeof x === "object" ? JSON.stringify(x) : x))
        .join("\n")
    : String(value ?? "");
const actions = {
  append_images: "Add images to this asset group",
  create_asset_group: "Create a new asset group",
  create_ad: "Create a new image ad",
};
const makeSet = () => ({
  id: crypto.randomUUID(),
  files: [],
  fields: {},
  excluded: false,
});

function CampaignPicker({ items, value, onChange }) {
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState(""),
    [active, setActive] = useState(0);
  const selected = items.find((x) => x.id === value);
  const filtered = items.filter((x) =>
    `${x.name} ${x.id}`.toLowerCase().includes(query.toLowerCase()),
  );
  const choose = (item) => {
    onChange(item.id);
    setOpen(false);
    setQuery("");
  };
  return (
    <div className="uc-combo">
      <label htmlFor="uc-campaign">Reference campaign</label>
      <input
        id="uc-campaign"
        role="combobox"
        aria-expanded={open}
        aria-controls="uc-campaign-options"
        aria-autocomplete="list"
        aria-activedescendant={
          open && filtered[active] ? `uc-campaign-${active}` : undefined
        }
        value={open ? query : selected?.name || ""}
        placeholder="Search campaigns…"
        onFocus={() => {
          setOpen(true);
          setQuery("");
          setActive(0);
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
        }}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActive((i) => Math.min(i + 1, filtered.length - 1));
          }
          if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => Math.max(0, i - 1));
          }
          if (e.key === "Enter" && open && filtered[active]) {
            e.preventDefault();
            choose(filtered[active]);
          }
        }}
      />
      {open && (
        <div className="uc-options" id="uc-campaign-options" role="listbox">
          {filtered.map((item, i) => (
            <div
              id={`uc-campaign-${i}`}
              role="option"
              aria-selected={item.id === value}
              key={item.id}
              className={i === active ? "active" : ""}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(item);
              }}
            >
              {item.name}
              <small>
                {item.id} · {item.status}
              </small>
            </div>
          ))}
          {!filtered.length && <p>No matching campaigns</p>}
        </div>
      )}
    </div>
  );
}
function ImagePreview({ file }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);
  return url ? <img className="uc-thumb" src={url} alt={file.name} /> : null;
}
function TextEntries({
  title,
  values = [],
  min,
  max,
  limit,
  readOnly,
  onChange,
}) {
  const entries = values.length ? values : [""];
  const replace = (index, next) => {
    const copy = [...entries];
    copy.splice(index, 1, ...next);
    onChange(copy.length ? copy : [""]);
  };
  const count = entries.filter((x) => x.trim()).length;
  return (
    <fieldset className="uc-line-field">
      <legend>{title}</legend>
      {entries.map((value, index) => {
        const length = [...value.trim()].length;
        return (
          <div className="uc-line-edit" key={index}>
            <input
              aria-label={`${title} ${index + 1}`}
              aria-invalid={length > limit}
              disabled={readOnly}
              value={value}
              onChange={(e) => replace(index, [e.target.value])}
              onPaste={(e) => {
                const text = e.clipboardData.getData("text");
                if (text.includes("\n")) {
                  e.preventDefault();
                  const input = e.target;
                  replace(
                    index,
                    (
                      value.slice(0, input.selectionStart) +
                      text +
                      value.slice(input.selectionEnd)
                    )
                      .replace(/\r/g, "")
                      .split("\n"),
                  );
                }
              }}
            />
            <span
              className={
                length > limit ? "uc-line-count uc-error" : "uc-line-count"
              }
              aria-label={`${title} ${index + 1}: ${length} of ${limit} characters`}
            >
              {length}/{limit}
            </span>
            {!readOnly && (
              <button
                type="button"
                className="cw-btn cw-btn-secondary cw-btn-sm"
                aria-label={`Remove ${title} ${index + 1}`}
                onClick={() => replace(index, [])}
              >
                ×
              </button>
            )}
          </div>
        );
      })}
      <small className={count < min || count > max ? "uc-error" : ""}>
        {count} of {max} entries · minimum {min}
      </small>
      {!readOnly && (
        <button
          type="button"
          className="cw-btn cw-btn-secondary cw-btn-sm"
          disabled={entries.length >= max}
          onClick={() => onChange([...entries, ""])}
        >
          Add{" "}
          {title === "Headlines"
            ? "headline"
            : title === "Long headlines"
              ? "long headline"
              : "description"}
        </button>
      )}
    </fieldset>
  );
}
function FieldEditor({ fields, onChange, readOnly, pmax, campaignBranding }) {
  return (
    <div className="uc-fields">
      {Object.entries(label)
        .filter(([key]) => key in fields)
        .map(([key, title]) => {
          const isArray = multiline.has(key),
            count = Array.isArray(fields[key]) ? fields[key].length : 0;
          const limit =
            key === "headlines"
              ? "30 characters each"
              : key === "longHeadlines" || key === "descriptions"
                ? "90 characters each"
                : "";
          if (limit) {
            const [min, max, length] =
              key === "headlines"
                ? pmax
                  ? [3, 15, 30]
                  : [1, 5, 30]
                : key === "longHeadlines"
                  ? [1, 5, 90]
                  : pmax
                    ? [2, 5, 90]
                    : [1, 5, 90];
            return (
              <TextEntries
                key={key}
                title={title}
                values={fields[key]}
                min={min}
                max={max}
                limit={length}
                readOnly={readOnly}
                onChange={(value) => onChange(key, value)}
              />
            );
          }
          return (
            <label key={key}>
              {title}
              {isArray && " — one per line"}
              {limit && (
                <small>
                  {count} entered · {limit}
                  {key === "headlines" && !pmax ? " (Google Ads API v22)" : ""}
                </small>
              )}
              {isArray ? (
                <textarea
                  disabled={
                    readOnly || (campaignBranding && key === "businessName")
                  }
                  rows={Math.min(6, Math.max(2, count))}
                  value={arrayValue(fields[key])}
                  onChange={(e) => onChange(key, e.target.value.split("\n"))}
                />
              ) : key === "callToAction" ? (
                <select
                  disabled={readOnly}
                  value={fields[key]}
                  onChange={(e) => onChange(key, e.target.value)}
                >
                  {[
                    "AUTOMATED",
                    "LEARN_MORE",
                    "GET_QUOTE",
                    "APPLY_NOW",
                    "SIGN_UP",
                    "CONTACT_US",
                    "SUBSCRIBE",
                    "DOWNLOAD",
                    "BOOK_NOW",
                    "SHOP_NOW",
                    "BUY_NOW",
                    "DONATE_NOW",
                    "ORDER_NOW",
                    "PLAY_NOW",
                    "SEE_MORE",
                    "START_NOW",
                    "VISIT_SITE",
                    "WATCH_NOW",
                  ].map((value) => (
                    <option key={value} value={value}>
                      {value.toLowerCase().replaceAll("_", " ")}
                    </option>
                  ))}
                </select>
              ) : key === "urlCustomParameters" ? (
                <textarea
                  disabled={readOnly}
                  rows={3}
                  value={(fields[key] || [])
                    .map((p) => `${p.key}=${p.value}`)
                    .join("\n")}
                  placeholder="key=value — one per line"
                  onChange={(e) =>
                    onChange(
                      key,
                      e.target.value
                        .split("\n")
                        .filter(Boolean)
                        .map((line) => {
                          const split = line.indexOf("=");
                          return {
                            key: split < 0 ? line : line.slice(0, split),
                            value: split < 0 ? "" : line.slice(split + 1),
                          };
                        }),
                    )
                  }
                />
              ) : (
                <input
                  disabled={
                    readOnly || (campaignBranding && key === "businessName")
                  }
                  value={fields[key] || ""}
                  onChange={(e) => onChange(key, e.target.value)}
                />
              )}
            </label>
          );
        })}
    </div>
  );
}
function AutomationControls({
  rows = [],
  off = [],
  onChange,
  campaign = false,
  confirmed,
  onConfirm,
}) {
  const disabled = new Set(off);
  if (campaign && disabled.has("TEXT_ASSET_AUTOMATION"))
    disabled.add("FINAL_URL_EXPANSION_TEXT_ASSET_AUTOMATION");
  return (
    <section className="cw-card" aria-label="Creative automation">
      <h3>Creative automation</h3>
      <p>
        {campaign
          ? "These controls affect every asset group in the destination campaign, including existing creative."
          : "Changes apply only to this new ad. The reference ad stays unchanged."}
      </p>
      {!rows.length && (
        <p>Automation settings unavailable. Reload before applying.</p>
      )}
      {rows.map((row) => (
        <label className="uc-auto-check" key={row.type}>
          <input
            type="checkbox"
            aria-label={`Turn off ${row.label}`}
            checked={disabled.has(row.type)}
            disabled={
              !row.editable ||
              (row.type === "FINAL_URL_EXPANSION_TEXT_ASSET_AUTOMATION" &&
                disabled.has("TEXT_ASSET_AUTOMATION"))
            }
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? [...off, row.type]
                  : off.filter((t) => t !== row.type),
              )
            }
          />
          <span>
            Turn off {row.label}
            <small>
              Current: {row.status} ({row.source}) → Proposed:{" "}
              {disabled.has(row.type) ? "Off (explicit opt-out)" : row.status}
            </small>
          </span>
        </label>
      ))}
      {campaign && off.length > 0 && (
        <label className="uc-auto-check">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => onConfirm(e.target.checked)}
          />
          <span>
            I understand these automation changes affect the entire campaign.
          </span>
        </label>
      )}
      <p>
        Turning off text customization also disables Final URL expansion. Some
        Google features require changes in Google Ads; unavailable controls are
        not treated as off.
      </p>
    </section>
  );
}
function AssetList({ assets = [] }) {
  return (
    <div className="uc-assets">
      {assets.map((a, i) => (
        <div key={`${a.asset}-${i}`}>
          {a.imageUrl && (
            <img
              className="uc-thumb"
              src={a.imageUrl}
              alt={a.fieldType || a.name || "Logo"}
            />
          )}
          <p>
            {a.fieldType || a.name} {a.text || ""}{" "}
            {a.youtubeVideoId ? `YouTube video: ${a.youtubeVideoId}` : ""}
          </p>
          <small>
            {a.policySummary?.approvalStatus || "Policy status unavailable"}
            {a.policySummary?.policyTopicEntries
              ?.map((x) => ` · ${x.topic}`)
              .join("")}
          </small>
        </div>
      ))}
    </div>
  );
}
function InheritedDetails({ value }) {
  const words = (value) =>
    String(value || "")
      .toLowerCase()
      .replaceAll("_", " ");
  return (
    <div>
      {value.logos && (
        <p>{value.logos.length} logo(s) reused from the reference.</p>
      )}
      {value.branding && (
        <p>
          {value.branding.filter((x) => x.fieldType !== "BUSINESS_NAME").length}{" "}
          brand image(s) inherited.
        </p>
      )}
      {value.videos && (
        <p>{value.videos.length} reference video(s) retained.</p>
      )}
      {value.signals && (
        <ul>
          {value.signals.map((signal, i) => (
            <li key={i}>
              {signal.searchTheme?.text
                ? `Search theme: ${signal.searchTheme.text}`
                : `Audience: ${signal.audience?.audience?.split("/").pop()}`}
            </li>
          ))}
        </ul>
      )}
      {value.adAutomation && (
        <>
          <strong>Ad automation</strong>
          {value.adAutomation.length ? (
            <ul>
              {value.adAutomation.map((setting, i) => (
                <li key={i}>
                  {words(setting.assetAutomationType)}:{" "}
                  {words(setting.assetAutomationStatus)}
                </li>
              ))}
            </ul>
          ) : (
            <p>Google Ads defaults; the reference has no explicit overrides.</p>
          )}
        </>
      )}
      {"leadFormOnly" in value && (
        <p>Lead form only: {value.leadFormOnly ? "Yes" : "No"}</p>
      )}
    </div>
  );
}
function Comparison({ summary, differencesOnly }) {
  const rows = { ...summary.referenceFields, ...summary.fields };
  return (
    <table className="uc-diff">
      <thead>
        <tr>
          <th>Field</th>
          <th>Reference</th>
          <th>New content</th>
        </tr>
      </thead>
      <tbody>
        {Object.keys(rows).map((key) => {
          const before = summary.referenceFields[key],
            after = summary.fields[key],
            changed = JSON.stringify(before) !== JSON.stringify(after);
          if (differencesOnly && !changed) return null;
          return (
            <tr key={key}>
              <th>{label[key] || key}</th>
              <td>{arrayValue(before) || "—"}</td>
              <td className={changed ? "changed" : ""}>
                {Array.isArray(after)
                  ? after.map((v, i) => (
                      <div
                        key={i}
                        className={
                          !Array.isArray(before) || !before.includes(v)
                            ? "new-line"
                            : ""
                        }
                      >
                        {arrayValue(v)}
                      </div>
                    ))
                  : arrayValue(after) || "—"}
              </td>
            </tr>
          );
        })}
        <tr>
          <th>Marketing images</th>
          <td>
            {summary.action === "append_images"
              ? "Existing images retained"
              : "Reference images remain in the original"}
          </td>
          <td className="changed">
            {summary.images.map((i, n) => (
              <div key={n}>
                + {i.name} · {i.role.replaceAll("_", " ").toLowerCase()}
              </div>
            ))}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
export default function UpdateCreativeWorkflow() {
  const { campaignId } = useParams(),
    navigate = useNavigate(),
    { getAccountIdToUse } = useAccount();
  const accountId = getAccountIdToUse(),
    [user] = useState(getUserData),
    userId = user?.userId || user?.id;
  const base = `/api/v2/campaigns/${campaignId}/creative-update`;
  const [data, setData] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [step, setStep] = useState(0);
  const [sets, setSets] = useState(() => [makeSet()]),
    [selectedId, setSelectedId] = useState(null),
    [sourceId, setSourceId] = useState("");
  const [references, setReferences] = useState([]),
    [search, setSearch] = useState(null),
    [status, setStatus] = useState("all");
  const [review, setReview] = useState(null),
    [contentReviewed, setContentReviewed] = useState(false),
    [reviewWarning, setReviewWarning] = useState(false),
    [diffOnly, setDiffOnly] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelError, setCancelError] = useState("");
  const [plannedActions, setPlannedActions] = useState({});
  const [automationOff, setAutomationOff] = useState([]);
  const [campaignAutomationConfirmed, setCampaignAutomationConfirmed] =
    useState(false);
  const [logos, setLogos] = useState([]);

  const [enableAfter, setEnableAfter] = useState(false),
    [feedback, setFeedback] = useState(""),
    [result, setResult] = useState(null);
  const selectionRequest = useRef(0),
    mounted = useRef(true);
  const selected =
    sets.find((x) => x.id === (selectedId || sets[0]?.id)) || sets[0];
  const pmax = data?.destination.advertisingChannelType === "PERFORMANCE_MAX";
  const storageKey = `creative-update:${accountId}:${campaignId}`;
  const request = async (path = "", body, form = false) => {
    const url = getApiUrl(base + path),
      params = { accountId, userId };
    const response =
      body === undefined
        ? await api.get(url, { params })
        : await api.post(url, body, {
            params,
            ...(form
              ? { headers: { "Content-Type": "multipart/form-data" } }
              : {}),
          });
    return response.data;
  };
  const problem = (e) =>
    e.response?.data?.error || e.message || "Request failed. Try again.";
  const act = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(problem(e));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  const invalidate = () => {
    setReview(null);
    setContentReviewed(false);
    setReviewWarning(false);
  };
  const patch = (id, values) => {
    setSets((prev) => prev.map((s) => (s.id === id ? { ...s, ...values } : s)));
    invalidate();
  };
  useEffect(() => {
    mounted.current = true;
    setSets([makeSet()]);
    setSelectedId(null);
    setReview(null);
    setResult(null);
    setStep(0);
    setReferences([]);
    setData(null);
    let live = true;
    setBusy(true);
    api
      .get(getApiUrl(base), { params: { accountId, userId } })
      .then(async (response) => {
        if (!live) return;
        setData(response.data);
        setSourceId(response.data.destination.id);
        const previous = response.data.pendingRunId || sessionStorage.getItem(storageKey);
        if (previous) {
          const r = await api.get(getApiUrl(`${base}/runs/${previous}`), {
            params: { accountId, userId },
          });
          if (live && r.data.status !== "review") {
            setResult(r.data);
            setReview(r.data);
            setStep(2);
          } else sessionStorage.removeItem(storageKey);
        }
      })
      .catch((e) => {
        if (live) setError(problem(e));
      })
      .finally(() => {
        if (live) setBusy(false);
      });
    return () => {
      live = false;
      mounted.current = false;
    };
    // Reinitialize when switching accounts or campaigns; file drafts never leave this page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, userId, campaignId]);
  useEffect(() => {
    if (!sourceId) return;
    let live = true;
    setReferences([]);
    selectionRequest.current++;
    api
      .get(getApiUrl(`${base}/references`), {
        params: { accountId, userId, campaignId: sourceId },
      })
      .then((r) => {
        if (live) setReferences(r.data.references);
      })
      .catch((e) => {
        if (live) setError(problem(e));
      });
    return () => {
      live = false;
    };
  }, [base, accountId, userId, sourceId]);
  useEffect(() => {
    const handler = (e) => {
      if (sets.some((s) => s.files.length) && !result) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [sets, result]);
  async function selectReference(ref) {
    const serial = ++selectionRequest.current,
      id = selected.id;
    await act(async () => {
      const details = await request(
        `/reference?campaignId=${encodeURIComponent(sourceId)}&referenceId=${encodeURIComponent(ref.id)}`,
      );
      if (serial !== selectionRequest.current) return;
      setSearch(null);
      patch(id, {
        reference: details.reference,
        fields: {
          ...details.fields,
          name: `${details.fields.name} ${sets.findIndex((s) => s.id === id) + 1}`,
        },
        logoChoice: undefined,
        logoFile: undefined,
        automationOff: [],
        campaignId: sourceId,
        adGroup:
          sourceId === data.destination.id
            ? details.reference.adGroup?.resourceName
            : "",
      });
    });
  }
  async function reviewDraft(nextStep = 2) {
    await act(async () => {
      const form = new FormData();
      let index = 0;
      const draftSets = sets.map((s) => ({
        ...s,
        files: undefined,
        logoFile: undefined,
        logoChoice: s.logoFile
          ? (() => {
              form.append("images", s.logoFile);
              return { fileIndex: index++ };
            })()
          : s.logoChoice,
        reference: undefined,
        referenceId: s.reference?.id,
        referenceFingerprint: s.reference?.fingerprint,
        fields: Object.fromEntries(
          Object.entries(s.fields).map(([k, v]) => [
            k,
            Array.isArray(v) && multiline.has(k)
              ? v.map((x) => x.trim()).filter(Boolean)
              : v,
          ]),
        ),
        fileIndexes: s.excluded
          ? []
          : s.files.map((file) => {
              form.append("images", file);
              return index++;
            }),
      }));
      form.append(
        "draft",
        JSON.stringify({
          sets: draftSets,
          automationOff,
          campaignAutomationConfirmed,
          mode: nextStep === 1 ? "prepare" : "review",
        }),
      );
      const r = await request("/review", form, true);
      setPlannedActions(
        Object.fromEntries(r.summaries.map((s) => [s.id, s.action])),
      );
      if (nextStep === 1)
        setSets((prev) =>
          prev.map((s) => {
            const summary = r.summaries.find((x) => x.id === s.id);
            return summary ? { ...s, fields: summary.fields } : s;
          }),
        );
      setReview(nextStep === 2 ? r : null);
      setStep(nextStep);
      setContentReviewed(false);
    });
  }
  async function apply() {
    if (!contentReviewed) {
      setReviewWarning(true);
      return;
    }
    sessionStorage.setItem(storageKey, review.id);
    await act(async () => {
      let r;
      try {
        r = await request(`/runs/${review.id}/apply`, {
          contentReviewed: true,
        });
      } catch (e) {
        try {
          const live = await request(`/runs/${review.id}`);
          if (live.status !== "review") setResult(live);
          else sessionStorage.removeItem(storageKey);
        } catch {}
        throw e;
      }
      setResult(r);
      if (
        enableAfter &&
        r.status === "verified" &&
        review.summaries.some((s) => s.action !== "append_images")
      ) {
        try {
          r = await request(`/runs/${review.id}/enable`, { confirm: true });
          setResult(r);
        } catch (e) {
          setResult(await request(`/runs/${review.id}`));
          throw e;
        }
      }
    });
  }
  const filtered = references
    .filter(
      (r) =>
        (status === "all" || r.status === status) &&
        `${r.name} ${r.adGroupName || ""}`
          .toLowerCase()
          .includes((search || "").toLowerCase()),
    )
    .sort(
      (a, b) =>
        (a.status === "ENABLED" ? 0 : 1) - (b.status === "ENABLED" ? 0 : 1),
    );
  const canContinue =
    sets.some((s) => !s.excluded) &&
    sets
      .filter((s) => !s.excluded)
      .every(
        (s) => s.files.length && s.reference && (!pmax ? s.adGroup : true),
      );
  const uploadLabel = pmax ? "Asset group" : "Ad";
  const selectedSetName = `${uploadLabel} ${sets.findIndex((s) => s.id === selected?.id) + 1}`;
  const moveImage = (fileIndex, destinationId) => {
    const destinationIndex = sets.findIndex((s) => s.id === destinationId);
    const file = selected.files[fileIndex];
    if (!file || destinationIndex < 0 || destinationId === selected.id) return;
    const sourceId = selected.id;
    setSets((previous) =>
      previous.map((set) => {
        if (set.id === sourceId)
          return { ...set, files: set.files.filter((_, i) => i !== fileIndex) };
        if (set.id === destinationId)
          return { ...set, files: [...set.files, file] };
        return set;
      }),
    );
    setSelectedId(destinationId);
    selectionRequest.current++;
    invalidate();
    setError("");
    setFeedback(
      `Moved “${file.name}” from ${selectedSetName} to ${uploadLabel} ${destinationIndex + 1}. You’re now viewing the destination. All images remain assigned until you move or remove them.`,
    );
  };
  const sameRef =
    selected?.reference &&
    sets
      .filter((s) => !s.excluded)
      .every((s) => s.reference?.id === selected.reference.id);
  return (
    <div className="copy-workflow uc-root">
      <div className="cw-main">
        <header className="cw-topbar">
          <button
            className="cw-btn cw-btn-secondary"
            onClick={() => {
              if (
                !sets.some((s) => s.files.length) ||
                result ||
                window.confirm(
                  "Leave this creative draft? Uploaded files and unsaved edits will be lost.",
                )
              )
                navigate(`/campaigns/${campaignId}`);
            }}
          >
            ← Campaign
          </button>
          <strong>Update creative</strong>
          <span>{data?.destination.name}</span>
        </header>
        <CopyStepper steps={steps} current={step} />
        <main className="uc-body" aria-busy={busy}>
          <fieldset className="uc-workspace" disabled={busy}>
            {error && (
              <div role="alert" className="uc-error">
                {error}
              </div>
            )}
            {busy && (
              <p role="status">
                {step === 2 ? "Checking Google Ads…" : "Loading / validating…"}
              </p>
            )}
            {!data && !busy && (
              <button
                className="cw-btn cw-btn-secondary"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            )}
            {data && step === 0 && (
              <>
                <h1>Upload creative and choose references</h1>
                <p>
                  Existing creative stays in place.{" "}
                  {pmax
                    ? "Add images where they fit, or create a new asset group using your chosen reference."
                    : "Create new image ads using the references you choose."}
                </p>
                <div className="uc-layout">
                  <section>
                    <div className="cw-card">
                      <div className="cw-card-h">
                        <strong>{pmax ? "Images" : "New ad"}</strong>
                      </div>
                      <div className="cw-card-b">
                        <div className="uc-tabs" hidden={sets.length === 1}>
                          {sets.map((s, i) => (
                            <button
                              key={s.id}
                              className={s.id === selected.id ? "selected" : ""}
                              aria-pressed={s.id === selected.id}
                              onClick={() => {
                                setSelectedId(s.id);
                                setSearch(null);
                                setFeedback("");
                                selectionRequest.current++;
                              }}
                            >
                              {uploadLabel} {i + 1} · {s.files.length}{" "}
                              {s.files.length === 1 ? "image" : "images"}
                              {s.excluded ? " · excluded" : ""}
                            </button>
                          ))}
                        </div>
                        <p role="status" aria-live="polite">
                          {feedback}
                        </p>
                        <label className="uc-upload">
                          Add images to {selectedSetName}
                          <input
                            aria-label={`Add images to ${selectedSetName}`}
                            type="file"
                            multiple
                            accept="image/jpeg,image/png,image/gif"
                            disabled={busy}
                            onChange={(e) => {
                              patch(selected.id, {
                                files: [
                                  ...selected.files,
                                  ...Array.from(e.target.files),
                                ],
                              });
                              e.target.value = "";
                            }}
                          />
                          <small>
                            JPG, PNG or GIF · up to 5,120 KB each. Dimensions
                            and Google Ads requirements are checked before
                            review.
                          </small>
                        </label>
                        <ul className="uc-files">
                          {selected.files.map((f, i) => (
                            <li key={`${f.name}-${i}`}>
                              <ImagePreview file={f} />
                              <span>
                                {f.name}{" "}
                                <small>{Math.round(f.size / 1024)} KB</small>
                              </span>
                              <label className="uc-move-image">
                                {pmax ? "Assign to asset group" : "Move to ad"}
                                <select
                                  aria-label={`${pmax ? "Assign" : "Move"} ${f.name} to ${pmax ? "asset group" : "ad"}`}
                                  value={selected.id}
                                  disabled={busy || sets.length < 2}
                                  onChange={(e) => moveImage(i, e.target.value)}
                                >
                                  {sets.map((set, index) => (
                                    <option key={set.id} value={set.id}>
                                      {uploadLabel} {index + 1}
                                      {set.excluded ? " · excluded" : ""}
                                    </option>
                                  ))}
                                </select>
                              </label>
                              <button
                                aria-label={`Remove ${f.name}`}
                                disabled={busy}
                                onClick={() => {
                                  patch(selected.id, {
                                    files: selected.files.filter(
                                      (_, n) => n !== i,
                                    ),
                                  });
                                }}
                              >
                                ×
                              </button>
                            </li>
                          ))}
                        </ul>
                        <button
                          className="cw-btn cw-btn-secondary cw-btn-sm"
                          disabled={busy || sets.length >= 10}
                          onClick={() => {
                            const s = makeSet();
                            setSets([...sets, s]);
                            setSelectedId(s.id);
                            setSearch(null);
                            setTimeout(
                              () =>
                                document
                                  .querySelector(".uc-upload input[type=file]")
                                  ?.focus(),
                              0,
                            );
                            setFeedback("");
                            invalidate();
                          }}
                        >
                          {pmax
                            ? "+ Add creatives for another asset group"
                            : "+ Add another ad"}
                        </button>
                      </div>
                    </div>
                    <div className="cw-card">
                      <div className="cw-card-b">
                        <CampaignPicker
                          items={data.campaigns}
                          value={sourceId}
                          onChange={setSourceId}
                        />
                        <div className="uc-filter">
                          <label>
                            Reference {pmax ? "asset group" : "ad"}
                            <input
                              value={search ?? selected.reference?.name ?? ""}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => setSearch(e.target.value)}
                              placeholder={`Search ${pmax ? "asset groups" : "ads"}…`}
                            />
                          </label>
                          <label>
                            Status
                            <select
                              value={status}
                              onChange={(e) => setStatus(e.target.value)}
                            >
                              <option value="all">All statuses</option>
                              <option value="ENABLED">Enabled</option>
                              <option value="PAUSED">Paused</option>
                            </select>
                          </label>
                        </div>
                        <div className="uc-references">
                          {filtered.map((r) => (
                            <button
                              disabled={busy}
                              key={r.id}
                              className={
                                selected.reference?.id === r.id
                                  ? "selected"
                                  : ""
                              }
                              onClick={() => selectReference(r)}
                            >
                              <strong>{r.name}</strong>
                              <small>
                                {r.adGroupName && `${r.adGroupName} · `}
                                {r.status}
                              </small>
                            </button>
                          ))}
                          {!filtered.length && <p>No matching references.</p>}
                        </div>
                      </div>
                    </div>
                  </section>
                  <aside className="cw-card">
                    <div className="cw-card-h">
                      <strong>SELECTED REFERENCE - DETAILS</strong>
                    </div>
                    <div className="cw-card-b">
                      {selected.reference ? (
                        <>
                          <h2>{selected.reference.name}</h2>
                          <p>{selected.reference.status}</p>
                          <dl>
                            {Object.entries(selected.fields)
                              .filter(([k]) =>
                                [
                                  "headlines",
                                  "longHeadlines",
                                  "descriptions",
                                  "finalUrls",
                                  "businessName",
                                ].includes(k),
                              )
                              .map(([k, v]) => (
                                <React.Fragment key={k}>
                                  <dt>{label[k]}</dt>
                                  <dd>{arrayValue(v)}</dd>
                                </React.Fragment>
                              ))}
                          </dl>
                          {pmax && (
                            <section
                              className="cw-card"
                              aria-label="Logo and business name"
                            >
                              <h3>Logo and business name</h3>
                              <AssetList
                                assets={
                                  data.destination.brandGuidelinesEnabled
                                    ? data.destination.brandAssets
                                    : selected.reference.links?.filter((x) =>
                                        [
                                          "LOGO",
                                          "BUSINESS_NAME",
                                          "LANDSCAPE_LOGO",
                                        ].includes(x.fieldType),
                                      )
                                }
                              />
                              <p>
                                {data.destination.brandGuidelinesEnabled
                                  ? "Campaign branding is inherited. Changes must be made in Google Ads."
                                  : "After checking image capacity, you can add or replace the logo if this update creates a new asset group. Adding images to an existing group preserves its branding."}
                              </p>
                              <button
                                className="cw-btn cw-btn-secondary"
                                disabled={busy || !canContinue}
                                onClick={() => reviewDraft(1)}
                              >
                                Check placement and review logo
                              </button>
                            </section>
                          )}
                          <h3>Existing reference assets</h3>
                          <p>
                            Individual assets; Google assembles them into ads.
                          </p>
                          <AssetList assets={selected.reference.links || []} />
                          <button
                            className="cw-btn cw-btn-secondary"
                            disabled={busy || sameRef}
                            onClick={() => {
                              setSets((prev) =>
                                prev.map((s) =>
                                  s.excluded
                                    ? s
                                    : {
                                        ...s,
                                        reference: selected.reference,
                                        fields: {
                                          ...selected.fields,
                                          name: `${selected.reference.name.slice(0, 100)} · new creative ${prev.indexOf(s) + 1}`,
                                        },
                                        campaignId: selected.campaignId,
                                        adGroup: selected.adGroup,
                                      },
                                ),
                              );
                              invalidate();
                              setFeedback(
                                "Reference applied to all included uploads.",
                              );
                            }}
                          >
                            Use reference for all included uploads
                          </button>
                        </>
                      ) : (
                        <p>Choose a reference to see its content.</p>
                      )}
                    </div>
                  </aside>
                </div>
                {!pmax && (
                  <div className="cw-card">
                    <div className="cw-card-b">
                      <label>
                        Destination ad group for this new ad
                        <select
                          value={selected.adGroup || ""}
                          onChange={(e) =>
                            patch(selected.id, { adGroup: e.target.value })
                          }
                        >
                          <option value="">Choose an ad group</option>
                          {data.adGroups.map((g) => (
                            <option value={g.resourceName} key={g.id}>
                              {g.name} · {g.status}
                            </option>
                          ))}
                        </select>
                      </label>
                      <p>
                        New ads use this ad group’s existing targeting and
                        settings.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
            {data && step === 1 && (
              <>
                <h1>Confirm & adjust</h1>
                {pmax && (
                  <AutomationControls
                    rows={data.destination.automation}
                    off={automationOff}
                    campaign
                    confirmed={campaignAutomationConfirmed}
                    onConfirm={(v) => {
                      setCampaignAutomationConfirmed(v);
                      invalidate();
                    }}
                    onChange={(v) => {
                      setAutomationOff(v);
                      setCampaignAutomationConfirmed(false);
                      invalidate();
                    }}
                  />
                )}
                {sets
                  .filter((s) => !s.excluded)
                  .map((s, i) => {
                    return (
                      <section className="cw-card" key={s.id}>
                        <div className="cw-card-h">
                          <strong>
                            {uploadLabel} {i + 1} · {s.reference.name}
                          </strong>
                        </div>
                        <div className="cw-card-b">
                          <p>
                            {plannedActions[s.id] === "append_images"
                              ? "Add images to this asset group. Its existing fields remain unchanged."
                              : pmax
                                ? "Create a new paused asset group. Edit the copied fields below."
                                : "Create a new paused image ad. Edit the copied fields below."}
                          </p>
                          {!pmax && (
                            <AutomationControls
                              rows={s.reference.automation}
                              off={s.automationOff || []}
                              onChange={(v) =>
                                patch(s.id, { automationOff: v })
                              }
                            />
                          )}
                          {pmax && (
                            <section aria-label="Branding">
                              <h3>Branding</h3>
                              <p>
                                Scope:{" "}
                                {data.destination.brandGuidelinesEnabled
                                  ? "Campaign"
                                  : "Asset group"}
                              </p>
                              <h4>Current branding</h4>
                              <AssetList
                                assets={
                                  data.destination.brandGuidelinesEnabled
                                    ? data.destination.brandAssets
                                    : s.reference.links?.filter((x) =>
                                        [
                                          "LOGO",
                                          "LANDSCAPE_LOGO",
                                          "BUSINESS_NAME",
                                        ].includes(x.fieldType),
                                      )
                                }
                              />
                              {!data.destination.brandGuidelinesEnabled &&
                              plannedActions[s.id] === "create_asset_group" ? (
                                <>
                                  <h4>Logo for new asset group</h4>
                                  {s.logoFile ? (
                                    <>
                                      <ImagePreview file={s.logoFile} />
                                      <p>
                                        {s.logoFile.name} · Pending review. Save
                                        paused after Google validation; approval
                                        to serve is separate.
                                      </p>
                                    </>
                                  ) : s.logoChoice === null ? (
                                    <p>
                                      No logo selected. A replacement is
                                      required.
                                    </p>
                                  ) : s.logoChoice ? (
                                    <AssetList
                                      assets={[
                                        logos.find(
                                          (x) => x.asset === s.logoChoice.asset,
                                        ) || { name: s.logoChoice.asset },
                                      ]}
                                    />
                                  ) : (
                                    <p>
                                      Reuse reference logos. Replace a missing
                                      or disapproved donor logo below.
                                    </p>
                                  )}
                                  <button
                                    className="cw-btn cw-btn-secondary"
                                    onClick={() =>
                                      patch(s.id, {
                                        logoFile: undefined,
                                        logoChoice: null,
                                      })
                                    }
                                  >
                                    Remove from new asset group
                                  </button>
                                  <label className="uc-upload">
                                    Replace logo
                                    <input
                                      aria-label="Replace logo"
                                      type="file"
                                      accept="image/png,image/jpeg,image/gif"
                                      onChange={(e) => {
                                        if (e.target.files[0])
                                          patch(s.id, {
                                            logoFile: e.target.files[0],
                                            logoChoice: undefined,
                                          });
                                        e.target.value = "";
                                      }}
                                    />
                                  </label>
                                  <button
                                    className="cw-btn cw-btn-secondary"
                                    onClick={() =>
                                      act(async () =>
                                        setLogos(
                                          (await request("/logos")).logos,
                                        ),
                                      )
                                    }
                                  >
                                    Load account logos
                                  </button>
                                  <label>
                                    Choose account logo
                                    <select
                                      value={s.logoChoice?.asset || ""}
                                      onChange={(e) => {
                                        if (e.target.value)
                                          patch(s.id, {
                                            logoFile: undefined,
                                            logoChoice: {
                                              asset: e.target.value,
                                            },
                                          });
                                      }}
                                    >
                                      <option value="">Select a logo…</option>
                                      {logos.map((x) => (
                                        <option key={x.asset} value={x.asset}>
                                          {x.name} ·{" "}
                                          {x.policySummary?.approvalStatus ||
                                            "Unknown status"}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  <p>
                                    Replacement affects only the new asset
                                    group. The donor logo is not deleted.
                                    Certificate or appeal requirements must be
                                    resolved in Google Ads.
                                  </p>
                                </>
                              ) : (
                                <p>
                                  Existing branding is preserved. Resolve
                                  missing or disapproved branding in Google Ads,
                                  then refresh.
                                </p>
                              )}
                              <button
                                className="cw-btn cw-btn-secondary"
                                onClick={() =>
                                  act(async () => {
                                    const fresh = await request("");
                                    const details = await request(
                                      `/reference?campaignId=${encodeURIComponent(s.campaignId)}&referenceId=${encodeURIComponent(s.reference.id)}`,
                                    );
                                    setData(fresh);
                                    setCampaignAutomationConfirmed(false);
                                    patch(s.id, {
                                      reference: details.reference,
                                    });
                                  })
                                }
                              >
                                Refresh branding
                              </button>
                            </section>
                          )}
                          <FieldEditor
                            fields={s.fields}
                            readOnly={plannedActions[s.id] === "append_images"}
                            pmax={pmax}
                            campaignBranding={
                              pmax && data.destination.brandGuidelinesEnabled
                            }
                            onChange={(key, value) =>
                              patch(s.id, {
                                fields: { ...s.fields, [key]: value },
                              })
                            }
                          />
                        </div>
                      </section>
                    );
                  })}
                <details className="cw-card">
                  <summary>Inherited campaign settings</summary>
                  <p>
                    Budget, targeting, and campaign-level branding remain on the
                    destination campaign. Explicit automation opt-outs are
                    applied as shown in review. PMax copies reference audience
                    signals and search themes into new asset groups. Branding
                    choices and retained videos are shown in review.
                  </p>
                  <pre>
                    {JSON.stringify(
                      {
                        brandGuidelinesEnabled:
                          data.destination.brandGuidelinesEnabled,
                        assetAutomationSettings:
                          data.destination.assetAutomationSettings,
                        campaignBrandAssets: data.destination.brandAssets,
                        trackingUrlTemplate:
                          data.destination.trackingUrlTemplate,
                        finalUrlSuffix: data.destination.finalUrlSuffix,
                      },
                      null,
                      2,
                    )}
                  </pre>
                </details>
              </>
            )}
            {step === 2 && review && (
              <>
                <h1>Review & apply</h1>
                <div className="uc-review-controls">
                  <label className="uc-check">
                    <input
                      type="checkbox"
                      checked={diffOnly}
                      onChange={(e) => setDiffOnly(e.target.checked)}
                    />
                    Show differences only
                  </label>
                  {!result && (
                    <button
                      className="cw-btn cw-btn-secondary"
                      disabled={busy}
                      onClick={() => {
                        setStep(1);
                        invalidate();
                      }}
                    >
                      Edit new fields
                    </button>
                  )}
                </div>
                {review.summaries?.map((s, i) => (
                  <section className="cw-card" key={s.id}>
                    <div className="cw-card-h">
                      <strong>
                        {uploadLabel} {i + 1} · {actions[s.action]}
                      </strong>
                    </div>
                    <div className="cw-card-b">
                      <div>
                        {sets
                          .find((set) => set.id === s.id)
                          ?.files.map((file, i) => (
                            <ImagePreview file={file} key={i} />
                          ))}
                      </div>
                      <p>
                        Reference: {s.referenceName}
                        {s.adGroup && ` · Destination: ${s.adGroup}`}
                      </p>
                      {s.action === "append_images" && (
                        <p className="uc-warning">
                          Existing images and fields are retained. New images
                          added to an enabled asset group may serve after Google
                          approval.
                        </p>
                      )}
                      <Comparison summary={s} differencesOnly={diffOnly} />
                      {s.automation?.length > 0 && (
                        <section>
                          <h3>
                            Creative automation ·{" "}
                            {pmax
                              ? "entire destination campaign"
                              : "new ad only"}
                          </h3>
                          {s.automation.map((row) => (
                            <p key={row.type}>
                              {row.label}: {row.status} →{" "}
                              <strong>{row.proposed}</strong>
                            </p>
                          ))}
                        </section>
                      )}
                      {s.branding && (
                        <section>
                          <h3>Branding · {s.branding.scope}</h3>
                          <AssetList assets={s.branding.reference || []} />
                          <p>
                            {s.branding.proposedLogo
                              ? `Replacement: ${s.branding.proposedLogo.name || s.branding.proposedLogo.asset || "Missing"} · ${s.branding.proposedLogo.status || s.branding.proposedLogo.policySummary?.approvalStatus || "Unknown"}`
                              : "Existing branding retained"}
                          </p>
                          <p>
                            Original branding is unchanged. Uploaded replacement
                            logos may be saved paused while pending approval.
                          </p>
                        </section>
                      )}
                      {s.inherited && (
                        <details>
                          <summary>
                            Inherited logos, signals and automation
                          </summary>
                          <InheritedDetails value={s.inherited} />
                        </details>
                      )}
                    </div>
                  </section>
                ))}
                {!result && (
                  <>
                    <div
                      className="cw-card"
                      role="region"
                      aria-label="Content review"
                    >
                      <div className="cw-card-h">
                        <strong>Content review</strong>
                      </div>
                      <div className="cw-card-b">
                        <label className="uc-check">
                          <input
                            type="checkbox"
                            checked={contentReviewed}
                            onChange={(e) => {
                              setContentReviewed(e.target.checked);
                              setReviewWarning(false);
                            }}
                          />
                          I reviewed the creative, text, URLs, references, and
                          proposed changes.
                        </label>
                        {reviewWarning && (
                          <p role="alert" className="uc-error">
                            Confirm the content review before applying.
                          </p>
                        )}
                      </div>
                    </div>
                    {review.summaries.some(
                      (s) => s.action !== "append_images",
                    ) && (
                      <label className="uc-check">
                        <input
                          type="checkbox"
                          checked={enableAfter}
                          onChange={(e) => setEnableAfter(e.target.checked)}
                        />
                        Enable newly created ads / asset groups after
                        verification. Otherwise, they remain paused.
                      </label>
                    )}
                  </>
                )}
                {result && (
                  <div className="cw-card">
                    <div className="cw-card-h">
                      <strong>
                        {result.status === "verified"
                          ? "Update verified"
                          : result.status === "applied"
                            ? "Created — verification pending"
                            : result.status === "cancelled"
                              ? "Update cancelled"
                              : result.status === "uncertain"
                                ? "Update needs attention"
                                : `Update status: ${result.status}`}
                      </strong>
                    </div>
                    <div className="cw-card-b">
                      <p>
                        {(result.result?.enablePending
                          ? "Enabling is not yet verified. Check status or retry Enable new entities; the status assignment is safe to repeat."
                          : result.result?.message) ||
                          (result.status === "verified"
                            ? result.result?.enabled
                              ? "New entities enabled."
                              : "New entities remain paused; appended images retain their asset group’s status."
                            : "Check status before starting another update.")}
                      </p>
                      {result.result?.results?.map((r) => (
                        <p key={r.id}>
                          {actions[r.action]} · {r.resourceName} · {r.status}
                        </p>
                      ))}
                      {result.status !== "cancelled" && <button
                        className="cw-btn cw-btn-secondary"
                        disabled={busy}
                        onClick={() => act(async () => {
                          setResult(await request(`/runs/${review.id}`));
                          setConfirmCancel(false);
                          setCancelError("");
                        })}
                      >
                        Check status
                      </button>}
                      {result.status === "uncertain" && (
                        <div>
                          <p>Cancel update clears this blocked attempt in Stellair. Any ads, asset groups or other changes already applied in Google Ads remain there. Cancellation does not remove or change your original ads.</p>
                          {!result.canCancel && <p role="status">
                            We’re waiting for the previous request to finish before cancellation is available.
                            {result.cancelAvailableAt
                              ? ` Check status after ${new Date(result.cancelAvailableAt).toLocaleTimeString()}.`
                              : " Check status to see whether cancellation is available."}
                          </p>}
                          {cancelError && <p role="alert">{cancelError} Check status before trying again.</p>}
                          {confirmCancel ? <div role="group" aria-label="Confirm cancellation">
                            <p>Cancel this update? Google Ads changes will remain. Starting another update could create duplicates.</p>
                            <button className="cw-btn cw-btn-secondary" disabled={busy}
                              onClick={() => { setConfirmCancel(false); setCancelError(""); }}>Keep waiting</button>
                            <button className="cw-btn cw-btn-primary" disabled={busy}
                              onClick={async () => {
                                setBusy(true);
                                setCancelError("");
                                try {
                                  setResult(await request(`/runs/${review.id}/cancel`, { confirm: true }));
                                  setConfirmCancel(false);
                                } catch (e) {
                                  setCancelError(problem(e));
                                } finally {
                                  if (mounted.current) setBusy(false);
                                }
                              }}>{busy ? "Cancelling…" : "Confirm cancel update"}</button>
                          </div> : <button className="cw-btn cw-btn-secondary" disabled={busy || !result.canCancel}
                            onClick={() => setConfirmCancel(true)}>Cancel update</button>}

                        </div>
                      )}
                      {result.status === "applied" && (
                        <button
                          className="cw-btn cw-btn-primary"
                          disabled={busy}
                          onClick={() =>
                            act(async () =>
                              setResult(
                                await request(`/runs/${review.id}/verify`, {}),
                              ),
                            )
                          }
                        >
                          Verify again
                        </button>
                      )}
                      {result.status === "verified" &&
                        !result.result?.enabled &&
                        review.summaries?.some(
                          (s) => s.action !== "append_images",
                        ) && (
                          <button
                            className="cw-btn cw-btn-secondary"
                            disabled={busy}
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Enable the new ads / asset groups created by this update?",
                                )
                              )
                                act(async () =>
                                  setResult(
                                    await request(`/runs/${review.id}/enable`, {
                                      confirm: true,
                                    }),
                                  ),
                                );
                            }}
                          >
                            Enable new entities
                          </button>
                        )}
                      {result.status === "cancelled" && <p>You can start another update. Check Google Ads first to avoid creating duplicates.</p>}
                      {["verified", "failed", "cancelled"].includes(result.status) && (
                        <button
                          className="cw-btn cw-btn-secondary"
                          onClick={() => {
                            sessionStorage.removeItem(storageKey);
                            window.location.reload();
                          }}
                        >
                          Start another update
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </fieldset>
        </main>
        <footer className="uc-footer">
          <span>
            {sets.filter((s) => !s.excluded).length} included uploads · additive
            changes only
          </span>
          <div>
            {step > 0 && !result && (
              <button
                className="cw-btn cw-btn-secondary"
                disabled={busy}
                onClick={() => setStep(step - 1)}
              >
                Back
              </button>
            )}
            {step === 0 && (
              <button
                className="cw-btn cw-btn-primary"
                disabled={busy || !canContinue}
                onClick={() => reviewDraft(1)}
              >
                Confirm & adjust →
              </button>
            )}
            {step === 1 && (
              <button
                className="cw-btn cw-btn-primary"
                disabled={busy}
                onClick={() => reviewDraft(2)}
              >
                Validate & review →
              </button>
            )}
            {step === 2 && !result && review && (
              <button
                className="cw-btn cw-btn-primary"
                disabled={busy}
                onClick={apply}
              >
                Apply creative update
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
